import i18n from './i18n';
import en from './locales/en.json';

/*
 * The API writes its messages in English. translateApiMessage() shows one of
 * them in the active UI language, using the `apiMessages`, `apiAiStatus` and
 * `apiFields` sections of the locale files — their English values are the
 * API's exact wording, with {{name}} where the API inserts a value. Text it
 * does not recognise (a new or free-form message) is returned unchanged, so it
 * still reads, in English.
 *
 * Adding a backend message: put its exact English text in en.json under
 * `apiMessages` and the translations under the same key in ro.json / sr.json.
 */

const escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// English text -> key, for messages without placeholders.
const EXACT = new Map();
// Messages with placeholders, most specific (longest fixed text) first.
const PATTERNS = [];

const addSection = (section) => Object.entries(en[section] || {}).forEach(([name, text]) => {
    // English plural forms repeat the API's single wording; index it once.
    if (name.endsWith('_one')) return;
    const key = `${section}.${name.replace(/_other$/, '')}`;
    const params = [...text.matchAll(/\{\{(\w+)\}\}/g)].map((m) => m[1]);
    if (!params.length) {
        EXACT.set(text, key);
        return;
    }
    const source = text.split(/\{\{\w+\}\}/).map(escapeRegExp).join('(.*?)');
    PATTERNS.push({ re: new RegExp(`^${source}$`), key, params, fixedLength: text.replace(/\{\{\w+\}\}/g, '').length });
});
addSection('apiMessages');
addSection('apiAiStatus');
PATTERNS.sort((a, b) => b.fixedLength - a.fixedLength);

// Field name as the API writes it in validation messages ("pickup time").
const FIELDS = new Map(Object.entries(en.apiFields || {}).map(([name, label]) => [label.toLowerCase(), `apiFields.${name}`]));

const translateField = (label) => {
    const key = FIELDS.get(label.trim().toLowerCase());
    return key ? i18n.t(key) : label;
};

export const translateApiMessage = (message) => {
    if (typeof message !== 'string' || !message) return message;
    if ((i18n.resolvedLanguage || i18n.language) === 'en') return message;

    const text = message.trim();
    const exact = EXACT.get(text);
    if (exact) return i18n.t(exact);

    for (const { re, key, params } of PATTERNS) {
        const match = text.match(re);
        if (!match) continue;
        const values = {};
        params.forEach((param, i) => {
            const value = match[i + 1];
            values[param] = param === 'field' ? translateField(value) : param === 'count' ? Number(value) : value;
        });
        return i18n.t(key, values);
    }
    return message;
};
